import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Toggle from "../../components/common/toggle";
import { HiDotsVertical } from "react-icons/hi";
import { IoEye } from "react-icons/io5";
import { MdOutlineModeEdit, MdDeleteOutline } from "react-icons/md";
import dist1 from "../../assets/images/categ1.png";
import { useViewProducts } from "../../hooks/products/products.showall";
import { useProductUpdate } from "../../hooks/products/products.update";
import { toast } from "react-hot-toast";
import { AiFillEdit } from "react-icons/ai";
import Pagination from "../../components/common/pagination";
import { useRemoveProduct } from "../../hooks/products/products.delete"
import Swal from "sweetalert2";
import { useRef } from "react";


const ProductList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching, refetch } = useViewProducts({ page, limit: pageSize, q: searchTerm });
  const { mutate: updateProductStatus } = useProductUpdate();
  const { mutate: removeProduct } = useRemoveProduct();

  useEffect(() => {
    if (!location.state?.refreshProductsList) return;

    setPage(1);
    setSearchTerm("");
    refetch();

    // Clear one-time state after refresh trigger is handled.
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate, refetch]);
  useEffect(() => {
    const list = data?.data?.data;
    
    if (Array.isArray(list)) {
      const formattedProducts = list.map((product) => ({
        id: product._id || product.id,
        thumbnail_image: product.thumbnail_image
          ? `${process.env.REACT_APP_BASE_IMAGE}/${product.thumbnail_image}`
          : dist1,
        name: product.title,
        slug: product.slug,
        status:
          typeof product.status === "boolean"
            ? product.status
              ? "active"
              : "inactive"
            : product.status === null || product.status === undefined || product.status === ""
            ? "active"
            : String(product.status).toLowerCase(),
      }));
      setProducts(formattedProducts);
    } else if (!isFetching && !isLoading) {
      setProducts([]);
    }

    const paginationData = data?.data?.pagination;
    if (paginationData) {
      setPagination((prev) => ({
        currentPage: Number(paginationData.currentPage) || prev.currentPage || 1,
        totalPages: Number(paginationData.totalPages) || prev.totalPages || 1,
        totalDocs: Number(paginationData.totalDocs) || prev.totalDocs || 0,
        limit: Number(paginationData.limit) || prev.limit || pageSize,
        hasNextPage: Boolean(paginationData.hasNextPage),
        hasPrevPage: Boolean(paginationData.hasPrevPage),
      }));

      const currentPageFromServer = Number(paginationData.currentPage);
      if (
        !Number.isNaN(currentPageFromServer) &&
        currentPageFromServer > 0 &&
        currentPageFromServer !== page
      ) {
        setPage(currentPageFromServer);
      }
    } else {
      setPagination((prev) => ({
        ...prev,
        currentPage: page,
        hasNextPage: false,
        hasPrevPage: false,
        totalPages: 1,
      }));
    }

    setActiveDropdown(null);
  }, [data, isFetching, isLoading, page, pageSize]);


  // for close click outside of dropdown

  const menuRef = useRef(null);


  useEffect (() => {

    const handleClickOutside = (event) => {
      if( menuRef.current && !menuRef.current.contains(event.target)){
        setActiveDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const trimmed = searchTerm.trim().toLowerCase();
    if (!trimmed) {
      setFilteredProducts(products);
      return;
    }

    setFilteredProducts(
      products.filter(
        (product) =>
          product.name.toLowerCase().includes(trimmed) ||
          product.slug.toLowerCase().includes(trimmed)
      )
    );
  }, [products, searchTerm]);

  // Helper function to get product ID
  const getProductId = (product) => product?.id || product?._id;

  // Handle search/filter
  const handleSearch = (e) => {
    const value = e.target ? e.target.value : e;
    setSearchTerm(value);
  };


  const handleDelete = (id, productName) => {
  Swal.fire({
    title: "Delete Product?",
    text: `Are you sure you want to delete "${productName}"?`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Yes, delete it",
    cancelButtonText: "Cancel",
  }).then((result) => {
    if (result.isConfirmed) {
      removeProduct(id, {
        onSuccess: () => {
          toast.success("Product deleted successfully!");
        },
        onError: () => {
          toast.error("Failed to delete product. Please try again.");
        },
      });
    }
  });
};


  const handleRowClick = (product) => {
    navigate(`/products/view/${getProductId(product)}`, {
      state: { product },
    });
  };

  const handleToggleStatus = (id, newStatus) => {
    setProducts((prev) =>
      prev.map((product) =>
        getProductId(product) === id
          ? { ...product, status: newStatus ? "active" : "inactive" }
          : product
      )
    );
    updateProductStatus(
      { id, body: { status: newStatus ? "active" : "inactive" } },
      {
        onSuccess: () => {
          toast.success("Product status updated!");
        },
        onError: () => {
          toast.error("Failed to update product status. Please try again.");
        },
      }
    );

    // TODO: Add API call to update status
  };

  const handleEditProduct = (product) => {
    navigate(`/products/EditProducts/${getProductId(product)}`, {
      state: { product },
    });
  };

  const totalPages = Number(pagination.totalPages) || 1;
  const currentPage = Number(pagination.currentPage) || page;
  const currentLimit = Number(pagination.limit) || pageSize;
  const visibleCount = filteredProducts.length;
  const showFullRange = !searchTerm.trim() && pagination.totalDocs > 0 && visibleCount > 0;
  const rangeStart = showFullRange ? (currentPage - 1) * currentLimit + 1 : 0;
  const rangeEnd = showFullRange ? rangeStart + visibleCount - 1 : 0;

  const handlePageChange = (nextPage) => {
    if (!Number.isInteger(nextPage)) return;
    if (nextPage < 1 || nextPage > totalPages) return;
    if (nextPage === currentPage) return;
    setPage(nextPage);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "40px 20px", color: "#888" }}>
        Loading products...
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
          flexWrap: "wrap",
          marginBottom: "12px",
        }}
      >
        <div style={{ flex: "1 1 600px", minWidth: "220px" }}>
          <input
            type="text"
            placeholder="Search by product name ..."
            value={searchTerm}
            onChange={handleSearch}
            style={{
              width: "100%",
              padding: "8px 10px",
              border: "1px solid #d9d9d9",
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
        </div>
        <button
          className="commonButton"
          onClick={() => navigate("/products/AddProduct")}
        >
          <AiFillEdit size={20} color="#ffffff" style={{ marginRight: 8 }} /> Add
          Product
        </button>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "12px",
        }}
      >
        <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "14px" }}>
          Items per page:
          <select
            value={pageSize}
            onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            style={{ padding: "4px 8px" }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={20}>20</option>
          </select>
        </label>
      </div>

      {/* Products Table */}
      <div className="distributor-table-container">
        <table className="distributor-table">
          <thead>
            <tr className="distributor-table-head-tr">
              <th className="distributor-th">Image</th>
              <th className="distributor-th">Name</th>
              <th className="distributor-th">Slug</th>
              <th className="distributor-th">Status</th>
              <th className="distributor-th distributor-th-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan="5" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                  {searchTerm ? "No products found matching your search." : "No products available."}
                </td>
              </tr>
            ) : (
              filteredProducts.map((product) => (
                <tr
                  key={getProductId(product)}
                  className="distributor-tr"
                  onClick={() => handleRowClick(product)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="distributor-td">
                    <img
                      src={product?.thumbnail_image || dist1}
                      alt={product.name}
                      className="distributor-td-img"
                    />
                  </td>
                  <td className="distributor-td distributor-company-name">
                    {product.name}
                  </td>
                  <td className="distributor-td">{product.slug}</td>
                  <td
                    className="distributor-td"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Toggle
                      checked={product.status === "active"}
                      onChange={(checked) =>
                        handleToggleStatus(getProductId(product), checked)
                      }
                    />
                  </td>
                  <td
                    className="distributor-td distributor-text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="distributor-action-container"
                      ref={
                        activeDropdown === getProductId(product) ? menuRef : null
                      }
                    >
                      <HiDotsVertical
                        className="distributor-action-icon"
                        color="#898989"
                        size={20}
                        onClick={(e) => {
                          e.stopPropagation();
                          setActiveDropdown(
                            activeDropdown === getProductId(product) 
                              ? null 
                              : getProductId(product)
                          );
                        }}
                        style={{ cursor: "pointer" }}
                      />
                      {activeDropdown === getProductId(product) && (
                        <div className="category-dropdown-menu distributor-action-dropdown-menu">
                          <div
                            className="category-dropdown-item distributor-dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/products/view/${getProductId(product)}`, {
                                state: { product },
                              });
                            }}
                          >
                            <IoEye className="icons" size={16} /> View
                          </div>
                          <div
                            className="category-dropdown-item distributor-dropdown-item"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditProduct(product);
                            }}
                          >
                            <MdOutlineModeEdit className="icons" size={16} /> Edit
                          </div>
                          <div
                            className="category-dropdown-item distributor-dropdown-item distributor-dropdown-item-delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(getProductId(product), product.name);
                            }}
                          >
                            <MdDeleteOutline className="icons" size={16} /> Delete
                          </div>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isFetching && !isLoading && (
        <div style={{ textAlign: "center", padding: "12px", color: "#888" }}>
          Loading page...
        </div>
      )}

      <div style={{ marginTop: "20px" }}>
        {visibleCount > 0 && (
          <div style={{ textAlign: "center", marginBottom: "10px", color: "#666", fontSize: "14px" }}>
            {showFullRange
              ? `Showing ${rangeStart}-${rangeEnd} of ${pagination.totalDocs} products`
              : `Showing ${visibleCount} result${visibleCount === 1 ? "" : "s"}`}
          </div>
        )}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>

      {/* No Results Message */}
      {filteredProducts.length === 0 && !searchTerm && (
        <div
          style={{
            textAlign: "center",
            padding: "40px 20px",
            color: "#999",
            borderRadius: "8px",
            backgroundColor: "#f9f9f9",
            marginTop: "20px",
          }}
        >
          <p>No products found. Start by adding a new product.</p>
        </div>
      )}
    </>
  );
};

export default ProductList;
